'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'
import type { Template } from '@/lib/types'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

type TTemplateTabsProps = {
  template: Template
}

/**
 * Template tabs component for Overview, Tips, and FAQ sections
 * @param template - Template data containing ai_overview, ai_tips, and ai_faq
 * @returns Template tabs with content switching
 */
export function TemplateTabs({ template }: TTemplateTabsProps) {
  const [activeTab, setActiveTab] = useState('overview')
  const t = useTranslations('modals.template-preview')
  const { dt } = useDynamicI18n()

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-700">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-white text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
          onClick={() => {
            setActiveTab('overview')
          }}
        >
          {t('tabs.overview')}
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'tips'
              ? 'border-white text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
          onClick={() => {
            setActiveTab('tips')
          }}
        >
          {t('tabs.tips')}
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'faq'
              ? 'border-white text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
          onClick={() => {
            setActiveTab('faq')
          }}
        >
          {t('tabs.faq')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="text-white space-y-4">
            {template.ai_overview ? (
              <>
                {template.ai_overview.title && (
                  <h3 className="text-xl font-semibold">
                    {dt(template.ai_overview.title)}
                  </h3>
                )}
                {template.ai_overview.content && (
                  <p className="text-gray-300 leading-relaxed">
                    {dt(template.ai_overview.content)}
                  </p>
                )}
                {template.ai_overview.features &&
                  template.ai_overview.features.length > 0 && (
                    <div>
                      <h4 className="text-lg font-medium mb-2">
                        {t('key_features')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-300">
                        {template.ai_overview.features.map((feature, index) => (
                          <li key={index}>{dt(feature)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                {template.ai_overview.usecases &&
                  template.ai_overview.usecases.length > 0 && (
                    <div>
                      <h4 className="text-lg font-medium mb-2">
                        {t('perfect_for')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-300">
                        {template.ai_overview.usecases.map((usecase, index) => (
                          <li key={index}>{dt(usecase)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
              </>
            ) : (
              <p className="text-gray-400">{t('no_overview')}</p>
            )}
          </div>
        )}
        {activeTab === 'tips' && (
          <div className="text-white space-y-4">
            {template.ai_tips ? (
              <>
                {template.ai_tips.summary && (
                  <p className="text-gray-300 leading-relaxed">
                    {dt(template.ai_tips.summary)}
                  </p>
                )}
                {template.ai_tips.points &&
                  template.ai_tips.points.length > 0 && (
                    <div>
                      <h4 className="text-lg font-medium mb-2">
                        {t('key_tips')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-300">
                        {template.ai_tips.points.map((point, index) => (
                          <li key={index}>{dt(point)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                {template.ai_tips.target_users &&
                  template.ai_tips.target_users.length > 0 && (
                    <div>
                      <h4 className="text-lg font-medium mb-2">
                        {t('target_users')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-300">
                        {template.ai_tips.target_users.map((user, index) => (
                          <li key={index}>{dt(user)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
              </>
            ) : (
              <p className="text-gray-400">{t('no_tips')}</p>
            )}
          </div>
        )}
        {activeTab === 'faq' && (
          <div className="text-white">
            {template.ai_faq &&
            template.ai_faq.items &&
            template.ai_faq.items.length > 0 ? (
              <Accordion type="single" collapsible className="w-full">
                {template.ai_faq.items.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="border-gray-700"
                  >
                    <AccordionTrigger className="text-left text-white hover:text-gray-300 py-4">
                      <span className="text-lg font-medium">
                        {dt(item.question)}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-300 leading-relaxed pb-4">
                      {dt(item.answer)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <p className="text-gray-400">{t('no_faq')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
